import React, { useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader } from '../components/ui/card';
import { ArrowLeft, Mail, KeyRound } from 'lucide-react';
import { useBranding } from '../context/BrandingContext';

const ForgotPasswordPage = () => {
  const { branding } = useBranding();
  const appName = branding?.app_name || 'BOM Maestro';
  const brandLogo = branding?.logo_data || '';
  const [email, setEmail] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/auth/forgot-password`, {
        email: email.trim().toLowerCase(),
        // iter77 — let the backend build the reset link against the actual origin
        // the user is hitting (preview, prod, custom domain) instead of a baked-in URL.
        redirect_base: window.location.origin,
      });
      setSubmitted(true);
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(typeof detail === 'string' ? detail : 'Please enter a valid email address.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-100 p-4" data-testid="forgot-password-page">
      <Card className="w-full max-w-md industrial-card">
        <CardHeader className="border-b border-slate-100 pb-4 space-y-3">
          {brandLogo && (
            <div className="flex items-center justify-center bg-white rounded-md py-3 px-4 border border-slate-200 mx-auto">
              <img src={brandLogo} alt={appName} className="h-10 max-w-[180px] object-contain" data-testid="forgot-brand-logo" />
            </div>
          )}
          <div className="text-center">
            <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-orange-100 text-orange-600 mb-2">
              <KeyRound className="w-5 h-5" />
            </div>
            <h1 className="text-2xl font-bold text-slate-900">Forgot your password?</h1>
            <p className="text-sm text-slate-500 mt-1">No worries — we'll send you a reset link.</p>
          </div>
        </CardHeader>
        <CardContent className="pt-5 space-y-4">
          {submitted ? (
            <div className="text-center space-y-3 py-3" data-testid="forgot-password-success">
              <div className="inline-flex items-center justify-center w-10 h-10 rounded-full bg-emerald-100 text-emerald-700">
                <Mail className="w-5 h-5" />
              </div>
              <p className="text-sm text-slate-700">
                If an account exists for <span className="font-medium mono">{email}</span>, a password reset link is on its way.
              </p>
              <p className="text-xs text-slate-500">The link expires in 1 hour. Check your spam folder if you don't see it.</p>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="fp-email">Email address</Label>
                <Input
                  id="fp-email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@company.com"
                  required
                  autoFocus
                  className="rounded-sm"
                  data-testid="forgot-password-email-input"
                />
              </div>
              {error && (
                <p className="text-xs text-red-600" data-testid="forgot-password-error">{error}</p>
              )}
              <Button
                type="submit"
                disabled={loading || !email}
                className="w-full bg-orange-500 hover:bg-orange-600 rounded-sm"
                data-testid="forgot-password-submit-btn"
              >
                {loading ? 'Sending...' : 'Send reset link'}
              </Button>
            </form>
          )}
          <Link
            to="/login"
            className="flex items-center justify-center gap-1 text-xs text-slate-500 hover:text-slate-900 mt-2"
            data-testid="forgot-password-back-link"
          >
            <ArrowLeft className="w-3 h-3" /> Back to sign in
          </Link>
        </CardContent>
      </Card>
    </div>
  );
};

export default ForgotPasswordPage;
